import React from 'react';
import {Modal} from 'reactstrap';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Check, Plugs, X} from '../../icons';
import {BOOSTER_STEP_FEED, BOOSTER_STEP_USE_IA} from './boosterSteps';
import {
    LIBRARY_READY, UNLOCK_CONFIGURE, UNLOCK_CONNECT, UNLOCK_FEED, UNLOCK_PLAN,
    connectPathFor, unlockKindFor,
} from './use_ia/useClientPromptLibrary';

/**
 * Lo que abren los dos chips de estado del HUD: «Tu IA» y «La cuenta».
 *
 * «Dos chips, no un menú perdido» (mockup): lo que está conectado se ve arriba, junto al
 * organismo, y un click lo despliega sin ir a la configuración. Es UN modal con dos modos, igual
 * que la ayuda, y todo lo que dice sale de datos que la página YA tiene: el inventario de
 * ingredientes de la biblioteca de prompts (el mismo que dibuja la banda «Usa tu IA») y si el
 * conector se usó recientemente con este cliente (el mismo `mcp_recently_used` del starter pack).
 *
 * Lo que NO dice, a propósito: cuántas personas usan ChatGPT o Claude con este cliente. El
 * mockup lo muestra y el backend no lo sabe — no hay uso por asistente, sólo por agencia.
 */

export const CONN_IA = 'ia';
export const CONN_ACCOUNT = 'account';

function IngredientRow({ingredient, clientId, missing, t, onOpenSources, onClose}) {
    const label = t(`prompt_library.ingredients.${ingredient}`, ingredient);
    const kind = missing ? unlockKindFor(ingredient) : null;
    const path = missing ? connectPathFor(ingredient, clientId) : null;

    let action = null;
    if (missing && kind === UNLOCK_CONNECT && ingredient === 'docs_drive') {
        // Drive se resuelve desde el panel de Fuentes de esta misma página, no desde una ruta.
        action = (
            <button type="button" className="iab-link"
                    onClick={() => { onClose(); onOpenSources({connect: true}); }}>
                {t('ia_booster.conn.action_connect')}
            </button>
        );
    } else if (missing && path) {
        action = (
            <Link className="iab-link" to={path} onClick={onClose}>
                {t(kind === UNLOCK_CONFIGURE
                    ? 'ia_booster.conn.action_configure'
                    : 'ia_booster.conn.action_connect')}
            </Link>
        );
    } else if (missing && kind === UNLOCK_FEED) {
        action = (
            <a className="iab-link" href={`#${BOOSTER_STEP_FEED}`} onClick={onClose}>
                {t('ia_booster.conn.action_feed')}
            </a>
        );
    } else if (missing && kind === UNLOCK_PLAN) {
        action = <span className="iab-term__note">{t('ia_booster.conn.action_plan')}</span>;
    }

    return (
        <li className={`iab-term ${missing ? '-missing' : ''}`}>
            <span className={`iab-term__dot ${missing ? '-warn' : '-ok'}`} aria-hidden="true"/>
            <div className="iab-term__body">
                <b>
                    {label}
                    <span className="iab-term__state">
                        {' · '}
                        {t(missing ? 'ia_booster.conn.missing_label' : 'ia_booster.conn.connected_label')}
                    </span>
                </b>
                {action && <p>{action}</p>}
            </div>
        </li>
    );
}

export function BoosterConnectionModal({
    mode,
    clientId,
    clientName,
    connected = false,
    library,
    onClose,
    onOpenSources = () => {},
}) {
    const {t} = useTranslation();
    const isOpen = mode === CONN_IA || mode === CONN_ACCOUNT;
    const client = clientName || t('ia_booster.this_client');
    const available = library && Array.isArray(library.available) ? library.available : null;
    const missing = (library && library.missing) || [];
    const known = Boolean(library) && library.status === LIBRARY_READY && available !== null;
    const label = (ing) => t(`prompt_library.ingredients.${ing}`, ing);

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            modalClassName="iab-modal"
            contentClassName="iab-modal__content"
            unmountOnClose
            aria-labelledby="iab-conn-title"
        >
            <div className="iab-modal__head">
                <span/>
                <button type="button" className="iab-modal__close" onClick={onClose}
                        aria-label={t('ia_booster.conn.close')}>
                    <X size={14} weight="bold"/>
                </button>
            </div>

            {mode === CONN_IA && (
                <div className="iab-modal__body">
                    <h3 className="iab-modal__title" id="iab-conn-title">{t('ia_booster.conn.ia_title')}</h3>
                    <p className="iab-modal__sub">
                        {t('ia_booster.conn.ia_sub', {client, interpolation: {escapeValue: false}})}
                    </p>
                    <ul className="iab-terms">
                        <li className="iab-term">
                            <span className={`iab-term__dot ${connected ? '-ok' : '-warn'}`} aria-hidden="true"/>
                            <div className="iab-term__body">
                                <b>{t(connected
                                    ? 'ia_booster.conn.connector_on_title'
                                    : 'ia_booster.conn.connector_off_title')}</b>
                                <p>{t(connected
                                    ? 'ia_booster.conn.connector_on_text'
                                    : 'ia_booster.conn.connector_off_text')}</p>
                            </div>
                        </li>
                        <li className="iab-term">
                            <span className="iab-term__dot -ia" aria-hidden="true"/>
                            <div className="iab-term__body">
                                <b>{t('ia_booster.conn.sees_title')}</b>
                                {/* `available === null` = no lo pude averiguar. No se dibuja una
                                    lista vacía: diría que el asistente no ve nada del cliente. */}
                                {known ? (
                                    <div className="iab-sees">
                                        {available.map(ing => (
                                            <span key={ing} className="iab-see">
                                                <Check size={11} weight="bold"/> {label(ing)}
                                            </span>
                                        ))}
                                        {missing.map(ing => (
                                            <span key={ing} className="iab-see -off">{label(ing)}</span>
                                        ))}
                                    </div>
                                ) : <p>{t('ia_booster.conn.sees_empty')}</p>}
                            </div>
                        </li>
                        <li className="iab-term">
                            <span className="iab-term__dot -warn" aria-hidden="true"/>
                            <div className="iab-term__body">
                                <b>{t('ia_booster.conn.tip_title')}</b>
                                <p>{t('ia_booster.conn.tip_text')}</p>
                            </div>
                        </li>
                    </ul>
                    <div className="iab-modal__actions">
                        <Link className="iab-btn -ghost" to="/dashbo-mcp" onClick={onClose}>
                            <Plugs size={14}/> {t('ia_booster.conn.connect_other')}
                        </Link>
                        <a className="iab-btn -ia" href={`#${BOOSTER_STEP_USE_IA}`} onClick={onClose}>
                            {t('ia_booster.conn.go_use')}
                        </a>
                    </div>
                </div>
            )}

            {mode === CONN_ACCOUNT && (
                <div className="iab-modal__body">
                    <h3 className="iab-modal__title" id="iab-conn-title">{t('ia_booster.conn.account_title')}</h3>
                    <p className="iab-modal__sub">
                        {t('ia_booster.conn.account_sub', {client, interpolation: {escapeValue: false}})}
                    </p>
                    {known ? (
                        <>
                            <ul className="iab-terms">
                                {available.map(ing => (
                                    <IngredientRow key={ing} ingredient={ing} clientId={clientId}
                                                   missing={false} t={t}
                                                   onOpenSources={onOpenSources} onClose={onClose}/>
                                ))}
                                {missing.map(ing => (
                                    <IngredientRow key={ing} ingredient={ing} clientId={clientId}
                                                   missing t={t}
                                                   onOpenSources={onOpenSources} onClose={onClose}/>
                                ))}
                            </ul>
                            {missing.length === 0 && (
                                <p className="iab-modal__note">{t('ia_booster.conn.all_connected')}</p>
                            )}
                        </>
                    ) : <p className="iab-modal__note">{t('ia_booster.conn.sees_empty')}</p>}
                </div>
            )}
        </Modal>
    );
}

export default BoosterConnectionModal;
